import { useEffect, useRef, useState } from 'react';

type Size = { width: number; height: number };

export function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Set initial size immediately
    const rect = el.getBoundingClientRect();
    setSize({ width: rect.width, height: rect.height });

    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      // Prefer borderBoxSize if available; fall back to contentRect
      const nextWidth = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
      const nextHeight = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;

      setSize((prev) =>
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight },
      );
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, size } as const;
}
